import { useEffect, useLayoutEffect, useRef, useState } from 'react'
import { CitatorAccordion } from './CitatorAccordion'
import { CitatorCaseListItem } from './CitatorCaseListItem'
import { ChevronDown } from '@/assets/icons'
import { buildCaselawStandaloneViewerURL } from '@/standalone-reference-viewer/caselaw/utils/build-caselaw-viewer-url'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { trackRiaCitatorSectionExpanded, trackRiaCitatorCaseSelected, resolveCitatorCaseId, RiaSourceContext } from '@/analytics/utils/ria-source-analytics'
import { trackCitatorTreatmentOutcomeClicked } from '@/analytics/utils/citator-analytics'
import { CitatorSurface, mapCaseReviewToTreatmentOutcome } from '@/analytics/enums/citator.enums'
import { RiaCitatorSection } from '@/analytics/enums/ria.enums'
import type { CitatorCitingCasesResponse, DisplayableCaseReview } from '@/citator/schema/case-review-schema'
import { CitatorCasesLoadingSkeleton } from './CitatorCasesLoadingSkeleton'

export type CitingCasesAccordionProps = {
  data: CitatorCitingCasesResponse | undefined
  isLoading: boolean
  isError: boolean
  refetch: () => void
  filterQuery: string
  analyticsContext?: RiaSourceContext | null
  /** Analytics surface where this accordion is rendered. */
  citatorSurface?: CitatorSurface
}

function CitingCasesBottomFade({ heightClassName = 'h-28' }: { heightClassName?: string }) {
  return <div aria-hidden="true" className={`pointer-events-none absolute inset-x-0 bottom-0 ${heightClassName} bg-gradient-to-b from-transparent to-white`} />
}

/**
 * Default-open accordion that previews citing cases up to the available preview height.
 * Short lists hug their content; overflowing lists clip with a bottom fade and
 * "Show all N cases" toggle. Filtering acts as expansion (no truncation while a query is active).
 */
export function CitingCasesAccordion({ data, isLoading, isError, refetch, filterQuery, analyticsContext, citatorSurface }: CitingCasesAccordionProps) {
  const { trackEvent } = useAnalytics()
  const [showAll, setShowAll] = useState(false)

  const filteredData = data?.filter((c) => c.name.toLowerCase().includes(filterQuery.toLowerCase())) ?? []
  const isFiltering = filterQuery.trim().length > 0
  // Filter mode acts like "show all" — no truncation, no toggle, no fade.
  const showAllInternal = showAll || isFiltering
  const hasPreviewRows = filteredData.length > 0
  const shouldMeasurePreview = !showAllInternal && hasPreviewRows

  const clipRef = useRef<HTMLDivElement | null>(null)
  const [isOverflowing, setIsOverflowing] = useState(false)
  const [hasMeasuredPreview, setHasMeasuredPreview] = useState(false)
  const [hasOverflowed, setHasOverflowed] = useState(false)
  const shouldClipPreview = shouldMeasurePreview && (!hasMeasuredPreview || isOverflowing)

  useLayoutEffect(() => {
    const element = clipRef.current
    if (!element) {
      setIsOverflowing(false)
      setHasMeasuredPreview(false)
      return
    }
    // Expanded wrapper hugs its content, so the overflow check would always be false.
    if (!shouldMeasurePreview) {
      setIsOverflowing(false)
      setHasMeasuredPreview(false)
      return
    }

    const measure = () => {
      const nextIsOverflowing = element.scrollHeight > element.clientHeight + 1
      setIsOverflowing(nextIsOverflowing)
      setHasMeasuredPreview(true)
      if (nextIsOverflowing) {
        setHasOverflowed(true)
      }
    }
    measure()

    const observer = new ResizeObserver(measure)
    observer.observe(element)
    return () => observer.disconnect()
  }, [filteredData.length, shouldMeasurePreview])

  // Keep the "Show less" toggle visible after expansion regardless of current overflow.
  const showToggle = showAll || (!isFiltering && (isOverflowing || hasOverflowed))

  // Re-clearing the filter should return the user to the truncated default state.
  useEffect(() => {
    if (isFiltering) setShowAll(false)
  }, [isFiltering])

  const trackSectionExpanded = () => {
    if (!analyticsContext) return
    trackRiaCitatorSectionExpanded({
      trackEvent,
      context: analyticsContext as RiaSourceContext,
      section: RiaCitatorSection.CitingCases,
      sectionCount: data?.length,
    })
  }

  const handleShowAllToggle = () => {
    if (!showToggle) return
    if (!showAll) {
      trackSectionExpanded()
    }
    setShowAll((value) => !value)
  }

  return (
    <CitatorAccordion
      title="Citing cases"
      count={data?.length}
      defaultOpen
      chevronExpanded={showAllInternal}
      fillHeight={isLoading || shouldClipPreview}
      onHeaderClick={handleShowAllToggle}
    >
      {isLoading && (
        <div className="relative flex-1 min-h-0 overflow-hidden">
          <CitatorCasesLoadingSkeleton rows={5} />
          <CitingCasesBottomFade heightClassName="h-[184px]" />
        </div>
      )}

      {isError && (
        <div className="py-2">
          <div className="text-red-700 text-sm mb-2">Could not load citing cases.</div>
          <button type="button" onClick={refetch} className="rounded-md bg-brand-500 px-3 py-1 text-sm font-semibold text-white hover:bg-brand-400">
            Retry
          </button>
        </div>
      )}

      {!isLoading && !isError && data && data.length === 0 && <div className="text-sm text-brand-neutral-700">No citing cases found.</div>}

      {!isLoading && !isError && data && data.length > 0 && filteredData.length === 0 && (
        <div className="text-sm text-brand-neutral-700">No matches for "{filterQuery}".</div>
      )}

      {!isLoading && !isError && filteredData.length > 0 && (
        <>
          <div
            ref={clipRef}
            // Collapsed overflow: clip to the preview height. Short lists and expanded lists hug their content.
            className={shouldClipPreview ? 'relative flex-1 min-h-0 overflow-hidden' : 'relative'}
          >
            <div className="flex flex-col gap-4">
              {filteredData.map((c, index) => {
                const href = buildCaselawStandaloneViewerURL(c, analyticsContext ?? undefined)
                const handleReviewClick = (review: DisplayableCaseReview) => {
                  if (!citatorSurface) return
                  trackCitatorTreatmentOutcomeClicked({
                    trackEvent,
                    surface: citatorSurface,
                    outcome: mapCaseReviewToTreatmentOutcome(review),
                  })
                }
                return (
                  <CitatorCaseListItem
                    key={`${c.parent_id ?? c.s3_path}-${index}`}
                    case={c}
                    number={index + 1}
                    href={href}
                    onLinkClick={() => {
                      const caseId = resolveCitatorCaseId({ parentId: c.parent_id, s3Path: c.s3_path })
                      if (!caseId || !analyticsContext) return
                      trackRiaCitatorCaseSelected({
                        trackEvent,
                        context: analyticsContext as RiaSourceContext,
                        section: RiaCitatorSection.CitingCases,
                        caseId,
                        caseTitle: c.name,
                      })
                    }}
                    onReviewClick={handleReviewClick}
                  />
                )
              })}
            </div>
            {/* Bottom fade overlay; signals that "Show all N cases" reveals more. */}
            {!showAllInternal && isOverflowing && <CitingCasesBottomFade />}
          </div>

          {showToggle && (
            <div className="flex justify-center pt-4">
              <button
                type="button"
                onClick={handleShowAllToggle}
                className="inline-flex items-center gap-1 text-sm font-semibold leading-5 text-brand-500 hover:text-brand-700 hover:underline"
              >
                <ChevronDown className={`w-4 h-4 transition-transform ${showAll ? 'rotate-180' : ''}`} />
                {showAll ? 'Show less' : `Show all ${filteredData.length} cases`}
              </button>
            </div>
          )}
        </>
      )}
    </CitatorAccordion>
  )
}
